import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Clock, XCircle, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { Badge, Button, Card, EmptyState, PageSpinner } from '@/components/ui';
import { isApiError } from '@/lib/api/envelope';
import { useClientProductAccess } from '@/features/client-portal/useClientProductAccess';
import {
  fetchMyRequests,
  acknowledgeRequests,
  createRequest,
  type ClientRequestDto,
  type RequestType,
} from './api';

const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  BALANCE_INCREASE: 'Balance Increase',
  EXTEND_EXPIRY: 'Extend Expiry',
  RENEWAL: 'Package Renewal',
  SUPPORT: 'Support Request',
  PRODUCT_ACCESS: 'Product Access Request',
};

const STATUS_TONE = {
  PENDING: 'warning' as const,
  COMPLETED: 'success' as const,
  REJECTED: 'danger' as const,
};

const STATUS_ICON = {
  PENDING: Clock,
  COMPLETED: CheckCircle,
  REJECTED: XCircle,
};

function statusTone(s: string) {
  return STATUS_TONE[s as keyof typeof STATUS_TONE] ?? ('neutral' as const);
}

function StatusIcon({ status }: { status: string }) {
  const Icon = STATUS_ICON[status as keyof typeof STATUS_ICON] ?? Clock;
  return <Icon className="h-4 w-4" />;
}

function RequestCard({ req }: { req: ClientRequestDto }) {
  const [expanded, setExpanded] = useState(false);
  const hasResponse = req.status !== 'PENDING';
  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-fg">
              {REQUEST_TYPE_LABELS[req.requestType] ?? req.requestType}
            </span>
            <Badge tone={statusTone(req.status)}>
              <StatusIcon status={req.status} />
              <span className="ml-1">{req.status}</span>
            </Badge>
            {hasResponse && !req.clientAcknowledged && (
              <Badge tone="primary">New response</Badge>
            )}
          </div>
          <p className="mt-1 text-xs text-fg-muted">
            {new Date(req.createdAt).toLocaleString()}
            {req.reviewedAt && ` · Reviewed ${new Date(req.reviewedAt).toLocaleString()}`}
          </p>
          <p className="mt-2 text-sm text-fg">{req.description}</p>
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="ml-2 shrink-0 text-fg-muted hover:text-fg"
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3 border-t border-border pt-3 text-xs">
          {req.requestedBalanceAmount != null && (
            <Row label="Requested credits" value={req.requestedBalanceAmount.toLocaleString()} />
          )}
          {req.requestedExpiryDate && (
            <Row label="Requested expiry" value={req.requestedExpiryDate} />
          )}
          {req.currentBalance != null && (
            <Row label="Balance at submission" value={req.currentBalance.toLocaleString()} />
          )}
          {req.currentExpiryDate && (
            <Row label="Expiry at submission" value={new Date(req.currentExpiryDate).toLocaleDateString()} />
          )}
          {hasResponse && (
            <>
              <div className="border-t border-border pt-2">
                <p className="font-medium text-fg-muted">Admin Response</p>
                {req.adminResponse && (
                  <p className="mt-1 text-fg">{req.adminResponse}</p>
                )}
                {req.approvedBalanceAmount != null && (
                  <Row label="Approved credits" value={req.approvedBalanceAmount.toLocaleString()} />
                )}
                {req.approvedExpiryDate && (
                  <Row label="Approved expiry" value={req.approvedExpiryDate} />
                )}
                {req.reviewedBy && (
                  <Row label="Reviewed by" value={req.reviewedBy} />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-fg-muted">{label}</span>
      <span className="font-medium text-fg">{value}</span>
    </div>
  );
}

const FORM_TYPES: { value: RequestType; label: string; needsBalance: boolean; needsExpiry: boolean; needsProduct: boolean }[] = [
  { value: 'BALANCE_INCREASE', label: 'Balance Increase', needsBalance: true, needsExpiry: false, needsProduct: false },
  { value: 'EXTEND_EXPIRY', label: 'Extend Expiry Date', needsBalance: false, needsExpiry: true, needsProduct: false },
  { value: 'RENEWAL', label: 'Package Renewal', needsBalance: true, needsExpiry: true, needsProduct: false },
  { value: 'SUPPORT', label: 'General Support', needsBalance: false, needsExpiry: false, needsProduct: false },
  { value: 'PRODUCT_ACCESS', label: 'Request Product Access', needsBalance: false, needsExpiry: false, needsProduct: true },
];

export function ClientRequestsPage() {
  const qc = useQueryClient();
  const { lockedProducts } = useClientProductAccess();
  const [page, setPage] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState<RequestType>('BALANCE_INCREASE');
  const [formDesc, setFormDesc] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formExpiry, setFormExpiry] = useState('');
  const [formProduct, setFormProduct] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['me', 'requests', page],
    queryFn: () => fetchMyRequests(page, 10),
  });

  const ackMutation = useMutation({
    mutationFn: acknowledgeRequests,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'requests'] });
      qc.invalidateQueries({ queryKey: ['me', 'requests-unread'] });
    },
  });

  const createMutation = useMutation({
    mutationFn: () => {
      const typeConf = FORM_TYPES.find((t) => t.value === formType)!;
      return createRequest({
        requestType: formType,
        description: formDesc || undefined,
        ...(typeConf.needsProduct && formProduct ? { requestedProductCode: formProduct } : {}),
        ...(typeConf.needsBalance && formAmount ? { requestedBalanceAmount: parseInt(formAmount, 10) } : {}),
        ...(typeConf.needsExpiry && formExpiry ? { requestedExpiryDate: formExpiry + 'T00:00:00Z' } : {}),
      });
    },
    onSuccess: () => {
      setShowForm(false);
      setFormDesc('');
      setFormAmount('');
      setFormExpiry('');
      setFormProduct('');
      setSubmitSuccess(true);
      setSubmitError(null);
      setTimeout(() => setSubmitSuccess(false), 4000);
      qc.invalidateQueries({ queryKey: ['me', 'requests'] });
      ackMutation.mutate();
    },
    onError: (err) => {
      setSubmitError(isApiError(err) ? err.firstErrorMessage : 'Failed to submit request.');
    },
  });

  const requests = query.data?.content ?? [];
  const totalPages = query.data?.totalPages ?? 1;
  const typeConf = FORM_TYPES.find((t) => t.value === formType)!;
  const hasUnread = requests.some((r) => r.status !== 'PENDING' && !r.clientAcknowledged);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-fg">My Requests</h1>
          <p className="mt-0.5 text-sm text-fg-muted">
            Submit and track your requests to Authmatech.
          </p>
        </div>
        <div className="flex gap-2">
          {hasUnread && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => ackMutation.mutate()}
              isLoading={ackMutation.isPending}
            >
              Mark all read
            </Button>
          )}
          <Button size="sm" onClick={() => setShowForm((v) => !v)}>
            <Plus className="h-4 w-4" />
            New request
          </Button>
        </div>
      </div>

      {submitSuccess && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-300">
          Request submitted. Our team will review it shortly.
        </div>
      )}

      {showForm && (
        <Card>
          <h2 className="mb-4 text-sm font-semibold text-fg">New Request</h2>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-fg-muted">Request Type</label>
              <select
                value={formType}
                onChange={(e) => setFormType(e.target.value as RequestType)}
                className="h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              >
                {FORM_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            {typeConf.needsProduct && (
              <div>
                <label className="mb-1 block text-xs font-medium text-fg-muted">
                  Product <span className="text-rose-500">*</span>
                </label>
                {lockedProducts().length === 0 ? (
                  <p className="text-sm text-fg-muted">All available products are already enabled for your account.</p>
                ) : (
                  <select
                    value={formProduct}
                    onChange={(e) => setFormProduct(e.target.value)}
                    className="h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  >
                    <option value="">Select a product…</option>
                    {lockedProducts().map((p) => (
                      <option key={p.productCode} value={p.productCode}>
                        {p.displayName || p.productCode}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {typeConf.needsBalance && (
              <div>
                <label className="mb-1 block text-xs font-medium text-fg-muted">
                  Requested Credits (optional)
                </label>
                <input
                  type="number"
                  min="1"
                  placeholder="e.g. 10000"
                  value={formAmount}
                  onChange={(e) => setFormAmount(e.target.value)}
                  className="h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                />
              </div>
            )}

            {typeConf.needsExpiry && (
              <div>
                <label className="mb-1 block text-xs font-medium text-fg-muted">
                  Requested Expiry Date (optional)
                </label>
                <input
                  type="date"
                  value={formExpiry}
                  onChange={(e) => setFormExpiry(e.target.value)}
                  className="h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                />
              </div>
            )}

            <div>
              <label className="mb-1 block text-xs font-medium text-fg-muted">
                {typeConf.needsProduct ? (
                  <>Tell us about your use case <span className="text-fg-subtle">(optional)</span></>
                ) : (
                  <>Description <span className="text-rose-500">*</span></>
                )}
              </label>
              <textarea
                rows={3}
                placeholder={
                  typeConf.needsProduct
                    ? 'Describe how you plan to use this product…'
                    : 'Describe your request...'
                }
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 resize-none"
              />
            </div>

            {submitError && (
              <p className="text-sm text-rose-600 dark:text-rose-400">{submitError}</p>
            )}

            <div className="flex gap-2">
              <Button
                size="sm"
                isLoading={createMutation.isPending}
                disabled={
                  (!typeConf.needsProduct && !formDesc.trim()) ||
                  (typeConf.needsProduct && !formProduct)
                }
                onClick={() => createMutation.mutate()}
              >
                Submit request
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setShowForm(false); setSubmitError(null); }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {query.isLoading ? (
        <PageSpinner />
      ) : requests.length === 0 ? (
        <EmptyState message="No requests yet. Use 'New request' to contact Authmatech about your balance, expiry, product access, or to get support." />
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <RequestCard key={req.id} req={req} />
          ))}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-fg-muted">Page {page + 1} of {totalPages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
